/**
 * LocaleBanner — Prioritet 5: Soft locale-mismatch banner
 * -------------------------------------------------------
 * Shown only when a visitor lands on a ccTLD whose locale differs from
 * their browser language AND they have NOT made an explicit choice yet.
 *
 * Example: a Danish-speaking user clicks a link to relark.se. The site
 * stays in Swedish (canonical for the domain — important for SEO/hreflang)
 * but a thin banner offers a one-click switch to relark.dk.
 *
 * Dismissals are remembered for the session so the banner never nags.
 */

import { useState } from "react";
import { X, Languages } from "lucide-react";
import {
  useGeoLocale,
  setGeoLocalePreference,
} from "@/hooks/useGeoLocale";
import { trackEvent } from "@/lib/analytics";
import { SeoLang } from "@/lib/seo-config";

const DISMISS_KEY = "td_geo_banner_dismissed";

// Self-localized message in the *suggested* language so the visitor
// reads it in the language they're about to switch to.
const COPY: Record<
  SeoLang,
  { message: string; switchCta: string; stayCta: string; dismissAria: string }
> = {
  da: {
    message: "Vil du se siden på dansk?",
    switchCta: "Skift til dansk",
    stayCta: "Bliv her",
    dismissAria: "Luk besked",
  },
  en: {
    message: "Would you like to view this in English?",
    switchCta: "Switch to English",
    stayCta: "Stay here",
    dismissAria: "Dismiss",
  },
  nb: {
    message: "Vil du se siden på norsk?",
    switchCta: "Bytt til norsk",
    stayCta: "Bli her",
    dismissAria: "Lukk melding",
  },
  sv: {
    message: "Vill du se sidan på svenska?",
    switchCta: "Byt till svenska",
    stayCta: "Stanna här",
    dismissAria: "Stäng meddelande",
  },
  de: {
    message: "Möchten Sie die Seite auf Deutsch ansehen?",
    switchCta: "Zu Deutsch wechseln",
    stayCta: "Hier bleiben",
    dismissAria: "Schließen",
  },
  fr: {
    message: "Voulez-vous voir cette page en français ?",
    switchCta: "Passer au français",
    stayCta: "Rester ici",
    dismissAria: "Fermer",
  },
  es: {
    message: "¿Quieres ver esta página en español?",
    switchCta: "Cambiar a español",
    stayCta: "Quedarme aquí",
    dismissAria: "Cerrar",
  },
  pt: {
    message: "Quer ver esta página em português?",
    switchCta: "Mudar para português",
    stayCta: "Ficar aqui",
    dismissAria: "Fechar",
  },
};

const FLAG: Record<SeoLang, string> = {
  da: "🇩🇰",
  en: "🇬🇧",
  nb: "🇳🇴",
  sv: "🇸🇪",
  de: "🇩🇪",
  fr: "🇫🇷",
  es: "🇪🇸",
  pt: "🇵🇹",
};

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export default function LocaleBanner() {
  const { mismatch, suggested, suggestedOrigin, active } = useGeoLocale();
  // Initialize from sessionStorage synchronously to avoid layout shift.
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  if (dismissed || !mismatch || !suggested || !suggestedOrigin) return null;

  const copy = COPY[suggested];

  const handleSwitch = () => {
    // Persist choice + emit override event BEFORE navigation.
    setGeoLocalePreference(suggested, "locale_banner");
    trackEvent("geo_locale_banner_switch", {
      from: active,
      to: suggested,
    });
    // Hard navigate to the canonical origin for the suggested locale.
    window.location.href = `${suggestedOrigin}/`;
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    trackEvent("geo_locale_banner_dismiss", {
      shown_for: suggested,
      stayed_on: active,
    });
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-accent/40 border-b border-border/60"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-5 py-2 text-sm">
        <p className="flex items-center gap-2 text-muted-foreground">
          <Languages className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{copy.message}</span>
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleSwitch}
            className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
          >
            <span aria-hidden="true">{FLAG[suggested]}</span>
            {copy.switchCta}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={copy.dismissAria}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}